import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Mail, Send, ShieldAlert, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function AdminSettings() {
  const { user, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [purgeOpen, setPurgeOpen] = useState(false);

  const isSuper = user.role === "super_admin";

  const load = () => {
    api.get("/admin/settings").then(({ data }) => setSettings(data));
    api.get("/admin/email-status").then(({ data }) => setEmailStatus(data));
  };
  useEffect(load, []);

  const setDelivery = async (mode) => {
    try {
      const { data } = await api.put("/admin/settings", { invitation_delivery: mode });
      setSettings(data);
      toast.success("Delivery mode updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    try {
      await api.post("/admin/invitations/test", { email: testEmail });
      toast.success(`Test invitation sent to ${testEmail}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const changePw = async () => {
    if (newPw.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.post("/auth/set-password", { password: newPw });
      setNewPw("");
      await refresh();
      toast.success("Password updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const purge = async (scope) => {
    try {
      const { data } = await api.post("/admin/purge-seed", { scope, keep_current_admin: true });
      toast.success(`Removed ${data.removed_users} demo users${scope === "all" ? ` and ${data.removed_courses} demo courses` : ""}`);
      setPurgeOpen(false);
      await refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!settings) return null;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Invitation delivery */}
      <div className="card-surface p-6" data-testid="delivery-settings">
        <h2 className="font-heading text-[18px] font-semibold">Invitation delivery</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose how invitations are issued. Copy-link mode always remains available for support and role testing.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            ["copy_link", "Copy link", "Show copyable magic links in the admin UI."],
            ["send_email", "Send email", "Deliver invitations by email via Resend."],
          ].map(([val, title, desc]) => (
            <button
              key={val}
              onClick={() => isSuper && setDelivery(val)}
              disabled={!isSuper}
              data-testid={`delivery-${val}`}
              className={cn("rounded-lg border p-4 text-left transition-colors",
                settings.invitation_delivery === val ? "border-[#33B6E4] bg-[#33B6E4]/8" : "border-border hover:bg-accent",
                !isSuper && "cursor-not-allowed opacity-60")}
            >
              <div className="font-medium">{title}</div>
              <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Test invitation */}
      <div className="card-surface p-6">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#33B6E4]" />
          <h2 className="font-heading text-[18px] font-semibold">Send a test invitation</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Verify deliverability before any bulk send.{" "}
          {emailStatus && !emailStatus.configured && (
            <span className="text-amber-600" data-testid="settings-email-not-configured-notice">Email is not configured. Supply RESEND_API_KEY to enable this.</span>
          )}
        </p>
        <div className="mt-4 flex gap-2">
          <input
            data-testid="test-email-input"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@example.com"
            className="h-10 flex-1 rounded-lg border border-input bg-card px-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <Button className="btn-brand h-10" onClick={sendTest} disabled={!emailStatus?.configured} data-testid="send-test-button">
            <Send className="mr-2 h-4 w-4" /> Send test
          </Button>
        </div>
      </div>

      {/* Change password */}
      <div className="card-surface p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-[#33B6E4]" />
          <h2 className="font-heading text-[18px] font-semibold">Change your password</h2>
        </div>
        {user.must_change_password && (
          <p className="mt-1 text-sm text-amber-600">You are using default credentials. Please change your password.</p>
        )}
        <div className="mt-4 flex gap-2">
          <input
            data-testid="new-password-input"
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (min 8 chars)"
            className="h-10 flex-1 rounded-lg border border-input bg-card px-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <Button className="h-10" onClick={changePw} data-testid="change-password-button">Update</Button>
        </div>
      </div>

      {/* Danger zone */}
      {isSuper && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6" data-testid="danger-zone">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            <h2 className="font-heading text-[18px] font-semibold text-destructive">Danger zone</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Remove demo accounts and demo community posts, optionally with the sample courses and resources. Your own account is always kept.
          </p>
          <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive" className="mt-4" data-testid="purge-seed-button">
                <Trash2 className="mr-2 h-4 w-4" /> Remove demo data
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="purge-confirm-modal">
              <DialogHeader><DialogTitle>Remove demo data?</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">
                Demo accounts, their posts and comments are permanently deleted. Choose whether the sample courses,
                lessons and resources go too. Real imported users and content are untouched. This cannot be undone.
              </p>
              <DialogFooter className="flex-col gap-2 sm:flex-row">
                <Button variant="outline" onClick={() => setPurgeOpen(false)}>Cancel</Button>
                <Button variant="destructive" onClick={() => purge("users_posts")} data-testid="confirm-purge-users-button">Users and posts only</Button>
                <Button variant="destructive" onClick={() => purge("all")} data-testid="confirm-purge-button">Everything, including courses</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
